import { Directive, EventEmitter, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, AbstractControlOptions, UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import isEqual from 'fast-deep-equal';
import { BehaviorSubject, Observable, Subject, Subscription, merge } from 'rxjs';
import { delay, filter, map, startWith, tap, withLatestFrom } from 'rxjs/operators';
import { Pattern } from '../../pattern';
import {
    ArrayPropertyKey,
    ControlMap,
    Controls,
    ControlsNames,
    ControlsType,
    FormErrors,
    FormUpdate,
    TypedAbstractControl,
    TypedFormGroup,
    isNullOrUndefined,
    takeUntilDestroyed,
} from '../managed-form/ngx-sub-form-utils';
import { FormGroupOptions, NgxFormWithArrayControls } from '../managed-form/ngx-sub-form.types';

type MapControlFunction<FormInterface, MapValue> = (ctrl: TypedAbstractControl<any>, key: keyof FormInterface) => MapValue;
type FilterControlFunction<FormInterface> = (ctrl: TypedAbstractControl<any>, key: keyof FormInterface, isCtrlWithinFormArray: boolean) => boolean;

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class ManagementFormComponent<ControlInterface, FormInterface = ControlInterface> extends Pattern implements OnInit, OnDestroy {
    public abstract dataInput: Required<ControlInterface> | null | undefined;
    // `Input` values are set while the `ngOnChanges` hook is ran
    // and it does happen before the `ngOnInit` where we start
    // listening to `dataInput$`. Therefore, it cannot be a `Subject`
    // or we will miss the first value
    protected dataInput$: BehaviorSubject<Required<ControlInterface> | null | undefined> = new BehaviorSubject<Required<ControlInterface> | null | undefined>(null);

    // called by the DataInput decorator
    /** @internal */
    public dataInputUpdated(data: Required<ControlInterface> | null | undefined): void {
        this.dataInput$.next(data);
    }

    public abstract dataOutput: EventEmitter<ControlInterface>;
    // using a private variable `_dataOutput$` to be able to control the
    // emission rate with a debounce or throttle for ex
    /** @internal */
    protected _dataOutput$: Subject<ControlInterface> = new Subject();

    @Input()
    public set disabled(shouldDisable: boolean | undefined) {
        this.setDisabledState(shouldDisable);
    }

    protected dataValue: ControlInterface | null = null;

    protected dataOriginalValue: FormInterface | null = null;

    private controlKeys: (keyof FormInterface)[] = [];

    public ngOnInit(): void {
        // we need to manually call registerOnChange because that function
        // handles most of the logic from NgxSubForm and when it's called
        // as a ControlValueAccessor that function is called by Angular itself

        this.registerOnChange((data) => this.onRegisterOnChangeHook(data));

        this.dataInput$
            .pipe(
                filter((newValue) => !isEqual(newValue, this.formGroup.value)),
                tap((newValue) => {
                    if (!isNullOrUndefined(newValue)) {
                        this.writeValue(newValue);
                    }
                }),
                takeUntilDestroyed(this)
            )
            .subscribe();

        this._dataOutput$
            .pipe(
                filter(() => this.formGroup.valid),
                tap((value) => {
                    this.dataOutput.emit(value);
                }),
                takeUntilDestroyed(this)
            )
            .subscribe();
    }

    /*********************************************************************/
    /** Traitement des événements Change provenants des champs de saisie */
    /*********************************************************************/

    private organization: Subscription | undefined = undefined;

    /**
     * Hook appellé lorsque le formulaire émet des valeurs.
     */
    protected onChange: Function | undefined = undefined;

    /**
     * Hook appellé lorsqu'un champ du formulaireest touché par l'utilisateur.
     */
    protected onTouched: Function | undefined = undefined;

    protected emitInitialValueOnInit = false;

    public get formControlNames(): ControlsNames<FormInterface> {
        // see @note form-group-undefined for as syntax
        return this.mapControls(
            (_, key) => key,
            () => true,
            false
        ) as ControlsNames<FormInterface>;
    }

    /**
     * Hook permettant de s'abonner aux changements de valeurs du formulaire.
     * @param data données émise par le formulaire.
     */
    protected onRegisterOnChangeHook(data: ControlInterface | null): boolean {
        // Ne fait rien, si le formulaire est invalide ou si la valeur émise par le formulaire est la même que celle spécifié à l'initialisation du formulaire.
        if (this.formGroup.invalid || isEqual(data, /*this.dataInput$.value*/ this.dataOriginalValue)) {
            return false;
        }

        this.dataValue = data;
        return true;
    }

    /**
     * Permet de s'abonner aux changements de valeurs du formulaire.
     * @param fn
     */
    public registerOnChange(fn: (_: any) => void): void {
        if (!this.formGroup) {
            return;
        }

        this.onChange = fn;

        interface KeyValueForm {
            key: keyof FormInterface;
            value: unknown;
        }

        const formControlNames: (keyof FormInterface)[] = Object.keys(this.formControlNames) as (keyof FormInterface)[];

        const formValues: Observable<KeyValueForm>[] = formControlNames.map((key) =>
            (this.formGroup.controls[key] as unknown as AbstractControl).valueChanges.pipe(
                startWith(this.formGroup.controls[key].value),
                map((value) => ({ key, value }))
            )
        );

        const lastKeyEmitted$: Observable<keyof FormInterface> = merge(...formValues.map((obs) => obs.pipe(map((x) => x.key))));

        this.organization = this.formGroup.valueChanges
            .pipe(
                // hook to give access to the observable for sub-classes
                // this allow sub-classes (for example) to debounce, throttle, etc
                this.handleEmissionRate(),
                startWith(this.formGroup.value),
                // this is required otherwise an `ExpressionChangedAfterItHasBeenCheckedError` will happen
                // this is due to the fact that parent component will define a given state for the form that might
                // be changed once the children are being initialized
                delay(0),
                filter(() => !!this.formGroup),
                // detect which stream emitted last
                withLatestFrom(lastKeyEmitted$),
                map(([_, keyLastEmit], index) => {
                    if (index > 0 && this.onTouched) {
                        this.onTouched();
                    }

                    if (index > 0 || (index === 0 && this.emitInitialValueOnInit)) {
                        if (this.onChange) {
                            this.onChange(
                                this.transformFromFormGroup(
                                    // do not use the changes passed by `this.formGroup.valueChanges` here
                                    // as we've got a delay(0) above, on the next tick the form data might
                                    // be outdated and might result into an inconsistent state where a form
                                    // state is valid (base on latest value) but the previous value
                                    // (the one passed by `this.formGroup.valueChanges` would be the previous one)
                                    this.formGroup.value
                                )
                            );
                        }

                        const formUpdate: FormUpdate<FormInterface> = {};
                        formUpdate[keyLastEmit] = true;
                        this.onFormUpdate(formUpdate);
                    }
                })
            )
            .subscribe();
    }

    /**
     * Permet de s'abonner aux événements Touch sur les contrôles du formulaire.
     * Non utilisé par défaut, à utiliser par le composant étendant cette classe.
     * @param fn
     */
    public registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /**
     * Permet de s'abonner aux événements émis lorsqu'un contrôle du formualire est mis à jour par l'utilisateur.
     * Non utilisé par défaut, à utiliser par le composant étendant cette classe.
     * @param fn
     */
    public onFormUpdate(formUpdate: FormUpdate<FormInterface>): void {}

    /**
     * Permet de modifier le taux d'émission des changements de valeur du formulaire.
     * Non utilisé par défaut, à utiliser par le composant étendant cette classe.
     * when customizing the emission rate of your sub form component, remember not to **mutate** the stream
     * it is safe to throttle, debounce, delay, etc but using skip, first, last or mutating data inside
     * the stream will cause issues!
     **/
    protected handleEmissionRate(): (obs$: Observable<FormInterface>) => Observable<FormInterface> {
        return (obs$) => obs$;
    }

    /**
     * Permet de transformer les valeurs emises par le formulaire dans un autre format.
     * Non utilisé par défaut, à utiliser par le composant étendant cette classe.
     **/
    protected transformFromFormGroup(formValue: FormInterface): ControlInterface | null {
        return formValue as unknown as ControlInterface;
    }

    /*********************************************************************/

    // when getDefaultValues is defined, you do not need to specify the default values
    // in your form (the ones defined within the `getFormControls` method)
    protected getDefaultValues(): Partial<FormInterface> | null {
        return null;
    }

    public writeValue(obj: Required<ControlInterface> | null): void {
        this.dataValue = obj;

        // @hack see where defining this.formGroup to undefined
        if (!this.formGroup) {
            return;
        }

        const defaultValues: Partial<FormInterface> | null = this.getDefaultValues();

        const transformedValue: FormInterface | null = this.transformToFormGroup(obj === undefined ? null : obj, defaultValues);

        this.dataOriginalValue = transformedValue;

        if (isNullOrUndefined(transformedValue)) {
            this.formGroup.reset(
                // calling `reset` on a form with `null` throws an error but if nothing is passed
                // (undefined) it will reset all the form values to null (as expected)
                defaultValues === null ? undefined : defaultValues,
                { emitEvent: false }
            );
        } else {
            this.replaceUndefinedValues(transformedValue);

            this.handleFormArrayControls(transformedValue);

            // The next few lines are weird but it's as workaround.
            // There are some shady behavior with the disabled state
            // of a form. Apparently, using `setValue` on a disabled
            // form does re-enable it *sometimes*, not always.
            // related issues:
            // https://github.com/angular/angular/issues/31506
            // https://github.com/angular/angular/issues/22556
            // but if you display `this.formGroup.disabled`
            // before and after the `setValue` is called, it's the same
            // result which is even weirder
            const fgDisabled: boolean = this.formGroup.disabled;

            this.formGroup.setValue(transformedValue, {
                emitEvent: false,
            });

            if (fgDisabled) {
                this.formGroup.disable();
            }
        }

        this.formGroup.markAsPristine();
        this.formGroup.markAsUntouched();
    }

    /**
     * Remplace les valeurs undefined par null.
     * @param obj
     */
    private replaceUndefinedValues(obj: any): void {
        Object.keys(obj).forEach((value, index) => {
            if (obj[value] == undefined) {
                obj[value] = null;
            }

            if (obj[value] == null && this.formGroup.disabled) {
                // En mode lecture, remplace les valeurs null par un tiret pour que les positions des libellés soent toutes identiques.
                obj[value] = '-';
            }

            if (obj[value] === '' && this.formGroup.disabled) {
                // En mode lecture, remplace les valeurs vide par un tiret pour que les positions des libellés soent toutes identiques.
                obj[value] = '-';
            }

            /*obj[value] = 'test';*/
        });
    }

    private handleFormArrayControls(obj: any) {
        Object.entries(obj).forEach(([key, value]) => {
            if (this.formGroup.get(key) instanceof UntypedFormArray && Array.isArray(value)) {
                const formArray: UntypedFormArray = this.formGroup.get(key) as UntypedFormArray;

                // instead of creating a new array every time and push a new FormControl
                // we just remove or add what is necessary so that:
                // - it is as efficient as possible and do not create unnecessary FormControl every time
                // - validators are not destroyed/created again and eventually fire again for no reason
                while (formArray.length > value.length) {
                    formArray.removeAt(formArray.length - 1);
                }

                for (let i = formArray.length; i < value.length; i++) {
                    if (this.formIsFormWithArrayControls()) {
                        formArray.insert(i, this.createFormArrayControl(key as ArrayPropertyKey<FormInterface>, value[i]));
                    } else {
                        formArray.insert(i, new UntypedFormControl(value[i]));
                    }
                }
            }
        });
    }

    // that method can be overridden if the
    // shape of the form needs to be modified
    protected transformToFormGroup(obj: ControlInterface | null, defaultValues: Partial<FormInterface> | null): FormInterface | null {
        let copy = {} as FormInterface;
        this.controlKeys.forEach((x) => {
            copy[x] = (obj as any as FormInterface)[x];
        });

        return copy as any as FormInterface;
    }

    // when developing the lib it's a good idea to set the formGroup type
    // to current + `| undefined` to catch a bunch of possible issues
    // see @note form-group-undefined
    public formGroup: TypedFormGroup<FormInterface> = new UntypedFormGroup(
        this._getFormControls(),
        this.getFormGroupControlOptions() as AbstractControlOptions
    ) as unknown as TypedFormGroup<FormInterface>;

    protected abstract getFormControls(): Controls<FormInterface>;

    private _getFormControls(): Controls<FormInterface> {
        const controls: Controls<FormInterface> = this.getFormControls();
        this.controlKeys = Object.keys(controls) as unknown as (keyof FormInterface)[];
        return controls;
    }

    /**
     * Extend this method to provide custom local FormGroup level validation
     */
    protected getFormGroupControlOptions(): FormGroupOptions<FormInterface> {
        return {};
    }

    // @todo could this be removed to avoid an override and just use `takeUntilDestroyed`?
    public ngOnDestroy(): void {
        // @hack there's a memory leak within Angular and those components
        // are not correctly cleaned up which leads to error if a form is defined
        // with validators and then it's been removed, the validator would still fail
        // `as any` if because we do not want to define the formGroup as FormGroup | undefined
        // everything related to undefined is handled internally and shouldn't be exposed to end user
        (this.formGroup as any) = undefined;

        if (this.organization) {
            this.organization.unsubscribe();
        }
    }

    public setDisabledState(shouldDisable: boolean | undefined): void {
        console.log('ManagementForm - setDisabledState : ', shouldDisable);
        if (!this.formGroup) {
            return;
        }

        if (shouldDisable) {
            this.formGroup.disable({ emitEvent: false });
        } else {
            this.formGroup.enable({ emitEvent: false });
        }
    }

    /************* VALIDATION **********************/

    public get formGroupErrors(): FormErrors<FormInterface> {
        const errors: FormErrors<FormInterface> = this.mapControls<ValidationErrors | ValidationErrors[] | null>(
            (ctrl) => ctrl.errors,
            (ctrl, _, isCtrlWithinFormArray) => (isCtrlWithinFormArray ? true : ctrl.invalid),
            true
        ) as FormErrors<FormInterface>;

        if (!this.formGroup.errors && (!errors || !Object.keys(errors).length)) {
            return null;
        }

        return Object.assign({}, this.formGroup.errors ? { formGroup: this.formGroup.errors } : {}, errors);
    }

    private mapControls<MapValue>(
        mapControl: MapControlFunction<FormInterface, MapValue>,
        filterControl: FilterControlFunction<FormInterface> = () => true,
        recursiveIfArray: boolean = true
    ): Partial<ControlMap<FormInterface, MapValue | MapValue[]>> | null {
        if (!this.formGroup) {
            return null;
        }

        const formControls: ControlsType<FormInterface> = this.formGroup.controls;
        const controls: Partial<ControlMap<FormInterface, MapValue | MapValue[]>> = {};

        for (const key in formControls) {
            if (this.formGroup.controls.hasOwnProperty(key)) {
                const control = formControls[key];

                if (recursiveIfArray && control instanceof UntypedFormArray) {
                    const values: MapValue[] = [];

                    for (let i = 0; i < control.length; i++) {
                        if (filterControl(control.at(i), key, true)) {
                            values.push(mapControl(control.at(i), key));
                        }
                    }

                    if (values.length > 0 && values.some((x) => !isNullOrUndefined(x))) {
                        controls[key] = values;
                    }
                } else if (control && filterControl(control, key, false)) {
                    controls[key] = mapControl(control, key);
                }
            }
        }
        return controls;
    }

    private formIsFormWithArrayControls(): this is NgxFormWithArrayControls<FormInterface> {
        return typeof (this as unknown as NgxFormWithArrayControls<FormInterface>).createFormArrayControl === 'function';
    }

    public manualSave(): void {
        if (!isNullOrUndefined(this.dataValue) && this.formGroup.valid) {
            this._dataOutput$.next(this.dataValue);
        }
    }
}
